import React, { createContext, useCallback, useContext } from 'react'

import { useOrganization } from 'hooks/organizations'
import { useUser } from 'hooks/user'

interface IFeatureFlag {
  key: string;
  value: boolean;
  description: string;
}

interface IFeatureFlagsContext {
  hasFeatureFlag(
    featureFlag: string,
    defaultValue?: boolean
  ): boolean;
}

const FeatureFlagsContext = createContext({} as IFeatureFlagsContext)

export const FeatureFlagsProvider: React.FC = ({ children }) => {
  const { user } = useUser()
  const { getCurrentOrganization } = useOrganization()

  const hasFeatureFlag = useCallback((featureFlag: string, defaultValue = false) => {
    // If there are no feature flags, return the default value
    if (!user.featureFlags) {
      return defaultValue
    }

    // Get the currently selected organization ID
    const organizationId = getCurrentOrganization()

    // If there's no organization ID, return the default value
    if (!organizationId) {
      return defaultValue
    }

    // Get the feature flags for the organization
    const orgFeatureFlags = user.featureFlags[organizationId]

    // If there are no feature flags for the organization, return the default value
    if (!orgFeatureFlags) {
      return defaultValue
    }

    // Find the feature flag
    const featureFlagObj = orgFeatureFlags.find((ff: IFeatureFlag) => ff.key === featureFlag)
    // If the feature flag is not found, return the default value
    if (!featureFlagObj) {
      return defaultValue
    }

    // Return the value of the feature flag
    return featureFlagObj.value
  }, [user, getCurrentOrganization])

  return (
    <FeatureFlagsContext.Provider value={{ hasFeatureFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export function useFeatureFlags (): IFeatureFlagsContext {
  const context = useContext(FeatureFlagsContext)

  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider')
  }

  return context
}
